import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Sifter from 'sifter';
import { debounce } from 'lodash';
import Header from './Header/Header';
import NoData from './NoData/NoData';
import Loading from './Loading/Loading';
import { Pagination } from './Pagination';
import { Grid } from '../../Utility/Grid';
import { viewFilterShape, sortFilterShape } from './lib/ListComponent.shapes';
import * as Constants from './lib/ListComponent.constants';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

function ListComponent(props) {
  const {
    useFilters: { view, sort },
    noDefaultSort,
    searchBy,
    initialSortFilterId,
    children,
    page,
    perPage,
    initialQueryValue = 'Default',
    defaultView,
    interactionHooks,
    data,
    initialViewFilterId
  } = props;

  /**
   * Resolver Functions
   *
   * We use these functions to gracefully merge the default props with any additional props
   * passed in. If a object prop is passed in whatsoever, there is no deep comparison/merge
   * performed by Babel at compile time–it simply checks to see if the prop exists and if it
   * does, use that instead of the defaults. This is a workaround to that to gracefully merge
   * them if only part of the object is passed in.
   * */
  const resolveFilters = () => {
    const useView = view || [];
    const useSort = sort || [];

    return {
      viewFilters: [...ListComponent.defaultProps.useFilters.view, ...useView],
      sortFilters: [...ListComponent.defaultProps.useFilters.sort, ...useSort]
    };
  };

  const resolveDefaultViewFilter = () => {
    const { viewFilters } = resolveFilters();
    if (initialViewFilterId) {
      const filterMatch = viewFilters.find((filter) => filter.id === initialViewFilterId);
      if (filterMatch) return filterMatch;
    }
    return viewFilters[0] || ListComponent.DEFAULT_VIEW_FILTER;
  };

  /**
   * By default, we force the relevance sort based on SifterJS' ratings
   * If you want to override this, you can pass a noDefaultSort prop
   * This sets the defaultSort to one that simply leaves the data alone.
   */
  const resolveDefaultSort = () => {
    const { sortFilters } = resolveFilters();
    /**
     * Here's how we resolve the default sort:
     * 1) If we've passed an initialSortFilter, this takes precendence. If the filterId is invalid, continue down the line
     * 2) If we've passed the noDefaultSort prop, return the NO SORT TYPE. Circumvents sorting.
     * 3) If we have not passed sifter fields but we have passed sort filters, the first sortFilter is the default
     * 4) If we have not passed sifter fields and have no sort filters, use NO SORT TYPE.
     * 5) Otherwise, if we have SifterFields, use the RELEVANCE SORT.
     */
    if (initialSortFilterId) {
      const filterMatch = sortFilters.find((filter) => filter.id === initialSortFilterId);
      if (filterMatch) return filterMatch;
    }

    if (noDefaultSort) return ListComponent.NO_SORT_TYPE;
    if (!searchBy.length && sortFilters.length) return sortFilters[0];
    if (!searchBy.length && !sortFilters.length) return ListComponent.NO_SORT_TYPE;
    return ListComponent.RELEVANCE_SORT_TYPE;
  };

  const [currentViewFilter, setCurrentViewFilter] = useState(resolveDefaultViewFilter);
  const [currentSortFilter, setCurrentSortFilter] = useState(resolveDefaultSort);
  const [currentSearchValue, setCurrentSearchValue] = useState(initialQueryValue);
  const [dropdownOpen, setDropdownOpen] = useState(Constants.DROP_DOWN_CLOSED);
  const [currentView, setCurrentView] = useState(defaultView);

  const resolveInteractionHooks = () => ({
    ...ListComponent.defaultProps.interactionHooks,
    ...interactionHooks
  });

  const handleDropdownOpen = (dropdownValue) => {
    const { onOpenDropdown } = resolveInteractionHooks();
    if (dropdownValue && dropdownValue !== dropdownOpen) {
      /** Set our state and fire interactions hook as a callback - sends the dropdown that was opened */
      // this.setState();

      setDropdownOpen({ dropdownOpen: dropdownValue }, () =>
        onOpenDropdown(dropdownValue, {
          currentViewFilter,
          currentSortFilter,
          currentSearchValue,
          dropdownOpen,
          currentView
        })
      );
    }
  };

  const handleDropdownClose = () => {
    const { onCloseDropdown } = resolveInteractionHooks();
    /** Set our state and fire interactions hook as a callback - sends back the dropdown that was closed */
    setDropdownOpen({ dropdownOpen: Constants.DROP_DOWN_CLOSED }, () =>
      onCloseDropdown(dropdownOpen, {
        currentViewFilter,
        currentSortFilter,
        currentSearchValue,
        dropdownOpen,
        currentView
      })
    );
  };

  const handleSortFilterClick = ({ target: { value } }) => {
    const { onSortFilterClick } = resolveInteractionHooks();
    if (value !== currentSortFilter) {
      setCurrentSortFilter({ currentSortFilter: value }, () => {
        /** Fire Interaction Hook */
        onSortFilterClick(value, {
          currentViewFilter,
          currentSortFilter,
          currentSearchValue,
          dropdownOpen,
          currentView
        });
      });
    }
  };

  /**
   * Event Handlers
   */
  const handleViewFilterClick = ({ target: { value } }) => {
    const { onViewFilterClick } = resolveInteractionHooks();
    if (value !== currentViewFilter) {
      setCurrentViewFilter({ currentViewFilter: value }, () => {
        /** Fire Interaction Hook */
        onViewFilterClick(value, {
          currentViewFilter,
          currentSortFilter,
          currentSearchValue,
          dropdownOpen,
          currentView
        });
      });
    }
  };

  const filterRegistry = () => {
    const { viewFilters, sortFilters } = resolveFilters();
    const defaultSortFilter = resolveDefaultSort();
    const sharedItems = {
      handleDropdownClose,
      handleDropdownOpen
    };

    return {
      viewFilterType: {
        ...sharedItems,
        defaultFilter: ListComponent.DEFAULT_VIEW_FILTER,
        filterCollection: viewFilters,
        openStatus: dropdownOpen === Constants.VIEW_FILTER_OPEN,
        openConst: Constants.VIEW_FILTER_OPEN,
        onChange: handleViewFilterClick
      },
      sortFilterType: {
        ...sharedItems,
        defaultFilter: defaultSortFilter,
        filterCollection: [
          ...(defaultSortFilter.id === Constants.RELEVANCE_SORT_ID ? [defaultSortFilter] : []),
          ...sortFilters
        ],
        openConst: Constants.SORT_FILTER_OPEN,
        openStatus: dropdownOpen === Constants.SORT_FILTER_OPEN,
        onChange: handleSortFilterClick
      }
    };
  };

  const triggerSearchUpdate = debounce(() => {
    const { onSearchChange } = resolveInteractionHooks();

    /** Fire interactions hook */
    onSearchChange(currentSearchValue, {
      currentViewFilter,
      currentSortFilter,
      currentSearchValue,
      dropdownOpen,
      currentView
    });
  }, ListComponent.DEBOUNCE_SEARCH_MS);

  /** Clear the debounced functions so it doesn't fire after unmounting */
  useEffect(() => () => triggerSearchUpdate.cancel(), []);

  /**
   * onChange is still fired as expected
   * triggerSearchUpdate is debounced in the constructor to avoid killing the API everytime we type
   * */
  const handleSearchChange = ({ target: { value } }) => {
    /** The interactions hook for the search change is in triggerSearchUpdate and is debounced */
    setCurrentSearchValue({ currentSearchValue: value }, triggerSearchUpdate);
  };

  const handleSearchClearValue = () => {
    const { currentSearchValue: searchPropsValue = '' } = currentSearchValue || {}
    if (searchPropsValue.length > 0) {
      setCurrentSearchValue({ currentSearchValue: '' }, triggerSearchUpdate);
    }
  };

  /**
   * Updates the view to the one passed in as an argument
   * Bound to the CARD_VIEW and TABLE_VIEW buttons in the ListHeader
   */
  const handleViewToggle = (viewName) => {
    const { onViewToggleClick } = resolveInteractionHooks();
    if (viewName !== currentView) {
      setCurrentView({ currentView: viewName }, () =>
        onViewToggleClick(viewName, {
          currentViewFilter,
          currentSortFilter,
          currentSearchValue,
          dropdownOpen,
          currentView
        })
      );
    }
  };

  const filterProps = {
    ...filterRegistry(),
    currentViewFilter,
    currentSortFilter,
    dropdownOpen
  };

  const { currentSearchValue: searchPropsValue } = currentSearchValue || {}

  const searchProps = {
    onChange: handleSearchChange,
    clearValue: handleSearchClearValue,
    value: searchPropsValue
  };
  const toggleProps = { handleViewToggle };

  const headerProps = {
    searchProps,
    toggleProps,
    filterProps,
    currentView,
    constants: Constants
  };

  const resolveSifter = (sortedAndFiltered) => {
    const sift = new Sifter(sortedAndFiltered);

    /**
     * SifterJS returns an object with a whole bunch of stuff.
     * Just grab the items portion - this has what we need to
     * return the results we want.
     * */
    const siftResults = sift.search(searchPropsValue, {
      fields: searchBy,
      nesting: true,
      respect_word_boundaries: true,
      conjunction: 'and'
    }).items;
    const resultIndexes = siftResults.map((item) => item.id);

    /**
     * Return a function to pass into data.filter
     * We additionally return the items here so we can use
     * the score values to sort by relevancy if the user has
     * that view filter selected.
     * */
    return {
      filter: (item, index) => resultIndexes.includes(index),
      items: siftResults
    };
  };

  const resolveSearchFilter = (sortedAndFiltered) => {
    if (!searchBy.length) {
      return { filter: () => true };
    }
    return resolveSifter(sortedAndFiltered);
  };

  /**
   * Method to prepare our data based on current filters, views, and search query
   * This is the method that actually invokes the data prop
   */
  const filterTheData = () => {
    const { filterFn } = currentViewFilter;
    const { id: sortId, sortFn } = currentSortFilter;

    const isRelevanceSort = sortId === Constants.RELEVANCE_SORT_ID;

    /**
     * If the filterFn and the sortFn are falsy, just pass the data through.
     * The sortFn defaults to 'falsy': null.
     * */
    if (!filterFn && !sortFn) {
      return data;
    }

    /** Apply our view filtering first */
    /** Next, apply our sorting if we haven't explicitly requested not to sort & the sortFn is not undefined */
    /** Resolve our granular filtering via the search field and return our filtered array */
    const filteredData = data.filter(filterFn);
    const sortedAndFiltered = sortFn !== undefined && !isRelevanceSort ? filteredData.sort(sortFn) : filteredData;
    const searchFilter = resolveSearchFilter(sortedAndFiltered);

    if (isRelevanceSort && searchBy) {
      return searchFilter.items.map((item) => sortedAndFiltered[item.id]);
    }

    return sortedAndFiltered.filter(searchFilter.filter);
  };

  const filteredData = filterTheData();
  const pagedData = perPage ? filteredData.slice((page - 1) * perPage, (page - 1) * perPage + perPage) : filteredData;

  const paginationProps = {
    page,
    perPage,
    itemTotal: filteredData.length
  };

  return (
    <Grid.ScreenClassRender
      render={(screenClass) =>
        children({
          screenClass, // enum [xl, lg, md, sm, xs]
          currentView, // enum [CARD_VIEW, TABLE_VIEW]
          allFilteredData: filteredData, // array of all filtered data []
          filteredData: pagedData, // array of filtered data [] on current page
          constants: Constants,
          searchProps,
          filterProps,
          headerProps,
          paginationProps
        })
      }
    />
  );
}

ListComponent.Header = Header;

ListComponent.NoData = NoData;

ListComponent.Loading = Loading;

ListComponent.Pagination = Pagination;

ListComponent.isMobile = (screenClass) => screenClass === 'sm';

ListComponent.DEBOUNCE_SEARCH_MS = 250;

ListComponent.DEFAULT_VIEW_FILTER = {
  id: Constants.DEFAULT_VIEW_FILTER_ID,
  label: 'All Items',
  filterFn: () => true
};

ListComponent.RELEVANCE_SORT_TYPE = {
  id: Constants.RELEVANCE_SORT_ID,
  label: Constants.RELEVANCE_SORT_LABEL,
  sortFn: null
};

ListComponent.NO_SORT_TYPE = {
  id: Constants.NO_SORT_ID,
  label: Constants.NO_SORT_LABEL,
  sortFn: undefined
};

ListComponent.propTypes = {
  /** Hooks for interactions */
  interactionHooks: PropTypes.shape({
    onViewToggleClick: PropTypes.func,
    onOpenDropdown: PropTypes.func,
    onCloseDropdown: PropTypes.func,
    onViewFilterClick: PropTypes.func,
    onSortFilterClick: PropTypes.func
  }),

  /** Children - excepts a function because */
  children: ReactChildrenPropType.isRequired,

  /** Data function to be called to fetch data. Must return a Promise */
  data: PropTypes.arrayOf(PropTypes.shape()),

  /** View to toggle by default – this is used as the SoT if view toggling is disabled */
  defaultView: PropTypes.oneOf([Constants.CARD_VIEW, Constants.TABLE_VIEW]),

  /** Initial Query Value */
  initialQueryValue: PropTypes.string,

  /** ID of the view filter that you want to display by default */
  initialViewFilterId: PropTypes.string,

  /** ID of the sort filter you want to display by default */
  initialSortFilterId: PropTypes.string,

  /** Flag to disable to internal default sort and default to 'native sorting' */
  noDefaultSort: PropTypes.bool,

  /** Current Page (regular numbers, don't worry about zero indexing) */
  page: PropTypes.number,

  /** perPage, results perpge */
  perPage: PropTypes.number,

  /**
   * Provide the accessor string for the property you want to run a basic RegExp.test against
   * This is a very basic Search function. If you wish to provide your own search function,
   * use the searchFn prop.
   * */
  searchBy: PropTypes.arrayOf(PropTypes.string),

  /** Filters Object */
  useFilters: PropTypes.shape({
    /** Array of sortFitlers which each have a label and a sortFn to sort the data */
    sort: PropTypes.arrayOf(sortFilterShape),

    /** Array of viewFilters which each have a label and a filterFn to sort the data */
    view: PropTypes.arrayOf(viewFilterShape)
  })
};

ListComponent.defaultProps = {
  interactionHooks: {
    onViewToggleClick: () => { },
    onOpenDropdown: () => { },
    onCloseDropdown: () => { },
    onViewFilterClick: () => { },
    onSortFilterClick: () => { },
    onSearchChange: () => { }
  },
  data: [],
  defaultView: Constants.CARD_VIEW,
  initialQueryValue: '',
  initialSortFilterId: undefined,
  initialViewFilterId: undefined,
  noDefaultSort: false,
  page: 0,
  perPage: undefined,
  searchBy: [],
  useFilters: {
    sort: [],
    view: []
  }
};

export default ListComponent;
